// lib/taskSort.mjs — R120 (Madel: "the tasks should be sorted by due date by
// default"; D33: due-first everywhere, plus a Sort select).
//
// One comparator for every task list on the Tasks page (List rows, the
// Board's open columns, My Work). Pure ESM, shared like lib/taskPatch.mjs
// (types: lib/taskSort.d.mts); tested by `node lib/taskSort.test.mjs`.
//
//   compareTasks(a, b) — the default ('due') order:
//     open (todo / in_progress / blocked) before closed;
//     OPEN:   due_date ascending as YYYY-MM-DD strings (overdue first, no
//             timezone math; no due date sorts last as '9999-12-31'), then
//             priority (urgent < high < normal < low; unknown = normal), then
//             due_time (timed before untimed, earlier first), then created_at
//             newest first, then id — so the order is deterministic;
//     CLOSED: completed_at (else updated_at) newest first, then id.
//   sortTasks(rows, mode) — a sorted COPY (the input is never mutated):
//     'due' (default) compareTasks · 'priority' the exact pre-R120 order
//     (priority, then due, then created newest — kept for anyone who wants it)
//     · 'updated' most recently changed first · 'created' newest first.
//     'updated' / 'created' still list open tasks before closed ones.

export const OPEN_STATUSES = ['todo', 'in_progress', 'blocked'];
export const PRIORITY_RANK = { urgent: 0, high: 1, normal: 2, low: 3 };
export const SORT_MODES = ['due', 'priority', 'updated', 'created'];
export const DEFAULT_SORT = 'due';
// Labels for the page's Sort select (one place, so tests and the page agree).
export const SORT_LABELS = { due: 'Due date', priority: 'Priority', updated: 'Recently updated', created: 'Newest' };

const NO_DUE = '9999-12-31';
const s = (v) => (v == null ? '' : String(v));
const isOpen = (t) => OPEN_STATUSES.includes(s(t && t.status));
const rank = (t) => {
  const r = PRIORITY_RANK[s(t && t.priority)];
  return r === undefined ? PRIORITY_RANK.normal : r;
};
const due = (t) => s(t && t.due_date) || NO_DUE;
const time = (t) => (/^\d{2}:\d{2}/.test(s(t && t.due_time)) ? s(t.due_time).slice(0, 5) : '');
const desc = (x, y) => (x < y ? 1 : x > y ? -1 : 0);
const asc = (x, y) => (x < y ? -1 : x > y ? 1 : 0);
const closedAt = (t) => s(t && (t.completed_at || t.updated_at));
const byId = (a, b) => asc(s(a && a.id), s(b && b.id));

// open before closed
function openFirst(a, b) { return Number(!isOpen(a)) - Number(!isOpen(b)); }
// timed before untimed, then earlier first
function byTime(a, b) {
  const ta = time(a), tb = time(b);
  if (ta && !tb) return -1;
  if (!ta && tb) return 1;
  return asc(ta, tb);
}

export function compareTasks(a, b) {
  const o = openFirst(a, b);
  if (o) return o;
  if (!isOpen(a)) return desc(closedAt(a), closedAt(b)) || byId(a, b);
  return asc(due(a), due(b))
    || (rank(a) - rank(b))
    || byTime(a, b)
    || desc(s(a && a.created_at), s(b && b.created_at))
    || byId(a, b);
}

// The pre-R120 TasksPage.sortOpen comparator, verbatim (no id tie-break — a
// stable sort keeps full ties in their input order, exactly as before).
function comparePriorityLegacy(a, b) {
  return openFirst(a, b)
    || (rank(a) - rank(b))
    || asc(due(a), due(b))
    || desc(s(a && a.created_at), s(b && b.created_at));
}
function compareUpdated(a, b) {
  return openFirst(a, b) || desc(s(a && (a.updated_at || a.created_at)), s(b && (b.updated_at || b.created_at))) || byId(a, b);
}
function compareCreated(a, b) {
  return openFirst(a, b) || desc(s(a && a.created_at), s(b && b.created_at)) || byId(a, b);
}

export function normSortMode(mode) {
  return SORT_MODES.includes(String(mode)) ? String(mode) : DEFAULT_SORT;
}

export function sortTasks(rows, mode = DEFAULT_SORT) {
  const list = Array.isArray(rows) ? rows.slice() : [];
  const m = normSortMode(mode);
  const cmp = m === 'priority' ? comparePriorityLegacy : m === 'updated' ? compareUpdated : m === 'created' ? compareCreated : compareTasks;
  return list.sort(cmp);
}
